import React, { useState, useEffect } from 'react';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import AddProductModal from '../components/products/AddProductModal';
import EditProductModal from '../components/products/EditProductModal';
import './Products.css';

// ✅ Use environment variable or fallback backend URL
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://agent-book-crm-backend.onrender.com';

const Products = () => {
  const [productsByType, setProductsByType] = useState({});
  const [activeType, setActiveType] = useState('');
  const [coverageTypes, setCoverageTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);

  // ✅ Load coverage types on mount
  useEffect(() => {
    async function loadCoverageTypes() {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API_BASE_URL}/api/coverage-types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load coverage types');
        const data = await res.json();
        if (Array.isArray(data)) {
          setCoverageTypes(data);
          if (data.length) setActiveType(data[0].name);
        }
      } catch (err) {
        console.error('Error loading coverage types:', err);
      }
    }
    loadCoverageTypes();
  }, []);

  // ✅ Fetch products for active type
  useEffect(() => {
    const fetchProducts = async () => {
      const token = localStorage.getItem('token');
      const ct = coverageTypes.find(ct => ct.name === activeType);
      if (!token || !ct) {
        setProductsByType(prev => ({ ...prev, [activeType]: [] }));
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/products?coverageTypeId=${ct.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error('Failed to load products');
        const products = await res.json();

        setProductsByType(prev => ({
          ...prev,
          [activeType]: Array.isArray(products) ? products : [],
        }));
      } catch (err) {
        console.error('Error fetching products:', err);
        setProductsByType(prev => ({ ...prev, [activeType]: [] }));
      }
    };

    fetchProducts();
  }, [activeType, coverageTypes]);

  // ✅ Add product and show immediately
  const handleNewProductSubmit = async newProductPayload => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newProductPayload),
      });

      if (!res.ok) throw new Error('Failed to add product');

      const insertedProduct = await res.json();
      const ctName = coverageTypes.find(ct => ct.id === insertedProduct.coverageTypeId)?.name;

      if (ctName) {
        setProductsByType(prev => ({
          ...prev,
          [ctName]: [...(prev[ctName] || []), insertedProduct],
        }));
      }
    } catch (err) {
      console.error('Error adding product:', err);
    }

    setShowAddModal(false);
  };

  // ✅ Edit existing product
  const handleEditSubmit = async updatedFields => {
    const token = localStorage.getItem('token');
    if (!token || !editingProduct) return;

    const payload = {
      ...updatedFields,
      coverageTypeId: editingProduct.coverage_type_id,
      carrierId: parseInt(updatedFields.carrierId, 10) || editingProduct.carrier_id,
      planName: updatedFields.planName || null,
      planId: updatedFields.planId || null,
      planType: updatedFields.planType || null,
      benefitOptions: updatedFields.benefitOptions || [],
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to update product');

      setProductsByType(prev => {
        const updatedList = (prev[activeType] || []).map(p =>
          p.id === editingProduct.id ? { ...p, ...payload } : p
        );
        return { ...prev, [activeType]: updatedList };
      });

      setEditingProduct(null);
    } catch (err) {
      console.error('Error updating product:', err);
    }
  };

  // ✅ Delete a product
  const handleDelete = async (productId, coverageTypeId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/products/${productId}?coverageTypeId=${coverageTypeId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) throw new Error('Failed to delete product');

      setProductsByType(prev => ({
        ...prev,
        [activeType]: (prev[activeType] || []).filter(p => p.id !== productId),
      }));
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  // ✅ Group products by carrier for cleaner rendering
  const renderProducts = products => {
    const grouped = products.reduce((acc, product) => {
      const carrier = product.carrier || 'Unknown Carrier';
      acc[carrier] = acc[carrier] || [];
      acc[carrier].push(product);
      return acc;
    }, {});
    
    return Object.entries(grouped).map(([carrier, items]) => {
      // Pick headers dynamically based on activeType
      let headers = ['Plan Name'];
      if (['MAPD', 'PDP'].includes(activeType)) headers.push('Plan ID');
      if (['MAPD', 'Individual Health', 'Life', 'Dental'].includes(activeType)) headers.push('Plan Type');
      if (activeType === 'Dental') headers.push('Benefit Options');

      return (
        <div key={carrier} className="carrier-accordion">
          <details>
            <summary>{carrier}</summary>
            <table className="product-table">
              <thead>
                <tr>
                  {headers.map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {items.filter(p =>
                  `${p.plan_name || ''} ${p.plan_id || ''} ${p.plan_type || ''} ${p.benefit_options || ''}`
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                ).map(product => (
                  <tr
                    key={product.id}
                    onClick={() => setEditingProduct(product)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{product.plan_name || '-'}</td>
                    {['MAPD', 'PDP'].includes(activeType) && <td>{product.plan_id || '-'}</td>}
                    {['MAPD', 'Individual Health', 'Life', 'Dental'].includes(activeType) && <td>{product.plan_type || '-'}</td>}
                    {activeType === 'Dental' && <td>{product.benefit_options || '-'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      );
    });
  };


  return (
    <>
      <Navbar />
      <Topbar />
      <div className="main-content">
        <h1>Products</h1>
        <div className="tabs">
          {coverageTypes.map(ct => (
            <button
              key={ct.id}
              className={`tab-button ${activeType === ct.name ? 'active' : ''}`}
              onClick={() => setActiveType(ct.name)}
            >
              {ct.name}
            </button>
          ))}
          <button className="add-button" onClick={() => setShowAddModal(true)}>Add Product</button>
        </div>

        <h2 style={{ marginTop: '20px' }}>{activeType} Plans</h2>

        <input
          type="text"
          className="search-input"
          placeholder={`Search ${activeType} plans...`}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <div className="product-section">
          {productsByType[activeType]?.length > 0
            ? renderProducts(productsByType[activeType])
            : <p className="no-data">No products for this type.</p>
          }
        </div>

        {showAddModal && (
          <AddProductModal
            coverageTypes={coverageTypes}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleNewProductSubmit}
          />
        )}

        {editingProduct && (
          <EditProductModal
            product={editingProduct}
            coverageTypes={coverageTypes}
            onClose={() => setEditingProduct(null)}
            onEdit={handleEditSubmit}
            onDelete={handleDelete}
          />
        )}
      </div>
    </>
  );
};

export default Products;
